/**
 * Chat history API — slim thread shell + on-demand artifact/audit hydration.
 */
import { buildApiUrl, tenantApiPrefix } from '../config';
import type {
  CalculationAuditReport,
  CopilotMessage,
  CopilotTimelineEntry,
  Finding,
  WorkspaceArtifact,
} from '../pages/copilot/stores/useCopilotStore';

export interface AuditStub {
  event_id: string;
  question: string;
  show_audit_link?: boolean | null;
  started_at: string;
  completed_at?: string | null;
}

export interface SlimThreadHistoryResponse {
  thread_id: string;
  thread_title?: string | null;
  agent_type?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  is_owner?: boolean | null;
  playbook_id?: string | null;
  playbook_run_id?: string | null;
  playbook_name?: string | null;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    attachment_ids?: string[];
    audit_id?: string;
    type?: string;
    statuses?: string[] | null;
    knowledge_items?: Array<{
      name: string;
      node_type: string;
      definition?: string;
      sql?: string;
      node_id?: string;
    }>;
  }>;
  artifacts: Array<{
    ref: string;
    event_id: string;
    type: string;
    title: string;
    created_at: string;
    frame_id?: string;
    row_count?: number;
    columns?: string[];
    connector_name?: string;
    connector_category?: string;
    chart_type?: string;
    metric_name?: string;
    playbook_id?: string;
    run_id?: string;
    artifact_id?: string;
    format?: string;
  }>;
  audits: AuditStub[];
  findings: Finding[];
  timeline: Array<
    | { type: 'user'; message_id: string }
    | { type: 'assistant'; message_id: string }
    | { type: 'artifact'; artifact_ref: string }
    | { type: 'sql_approval'; id: string }
    | { type: 'voice_call_approval'; id: string }
    | { type: 'voice_call'; id: string }
    | { type: 'download'; id: string }
    | { type: 'workbook'; id: string }
  >;
  pending_sql_approval?: {
    timeline_id: string;
    sql: string;
    title: string;
    connector_id: string;
    connector_name: string;
    connector_type: string;
    connector_category: string;
    is_file_connector: boolean;
    entity_id: string;
    entity_name: string;
    columns: string[];
    resource: string;
    properties: string[];
    limit?: number | null;
  } | null;
  pending_voice_call_approval?: {
    timeline_id: string;
    carrier_name: string;
    phone_number: string;
    objective: string;
    policy_number?: string;
    policyholder?: string;
  } | null;
  /** Workbooks the agent built in this thread. Stored, so they outlive the session. */
  workbooks?: Array<{
    id: string;
    workbook_id: string;
    title: string;
    filename: string;
    version: number;
    sheets: Array<{ name: string; row_count: number }>;
    size_bytes: number;
  }>;
  downloads?: Array<{
    id: string;
    frame_id: string;
    title: string;
    row_count: number;
    column_count: number;
    format: string;
    max_rows?: number | null;
    alternatives?: Array<{
      frame_id: string;
      title: string;
      row_count: number;
      column_count?: number;
    }>;
  }>;
  voice_calls?: Array<{
    call_id: string;
    carrier_name: string;
    phone_number: string;
    policy_number?: string;
    objective: string;
    status: string;
    duration_secs?: number | null;
    turn_count: number;
    extracted: Record<string, unknown>;
  }>;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchThreadHistory(
  tenantId: string,
  threadId: string,
): Promise<SlimThreadHistoryResponse> {
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/chat/threads/${threadId}/history`),
    { headers: authHeaders() },
  );
  if (!resp.ok) {
    throw new Error(`Failed to load thread history (${resp.status})`);
  }
  return resp.json();
}

export async function fetchThreadArtifact(
  tenantId: string,
  threadId: string,
  eventId: string,
): Promise<Record<string, unknown>> {
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/chat/threads/${threadId}/artifacts/${eventId}`),
    { headers: authHeaders() },
  );
  if (!resp.ok) {
    throw new Error(`Failed to load artifact (${resp.status})`);
  }
  return resp.json();
}

export async function fetchThreadAudit(
  tenantId: string,
  threadId: string,
  eventId: string,
): Promise<CalculationAuditReport> {
  const resp = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/chat/threads/${threadId}/audits/${eventId}`),
    { headers: authHeaders() },
  );
  if (!resp.ok) {
    throw new Error(`Failed to load audit (${resp.status})`);
  }
  const data = await resp.json();
  return mapAuditResponse(data);
}

function mapAuditResponse(raw: Record<string, unknown>): CalculationAuditReport {
  const story = (raw.story || {}) as Record<string, unknown>;
  const mapStory = (s: Record<string, unknown>) => {
    const ds = s.data_sources as Record<string, unknown> | undefined;
    const staging = s.staging as Record<string, unknown> | undefined;
    const calc = s.calculation as Record<string, unknown> | undefined;
    const outputs = s.outputs as Record<string, unknown> | undefined;
    return {
      headline: s.headline as string | undefined,
      dataSources: ds
        ? {
            summary: (ds.summary as string) || '',
            connectors: (ds.connectors as string[]) || [],
            tablesOrEndpoints: (ds.tables_or_endpoints as string[]) || [],
            details: ((ds.details as unknown[]) || []).map((d: any) => ({
              name: d.name || '',
              connectorType: d.connector_type || '',
              records: d.records || 0,
              columns: d.columns || 0,
              frameId: d.frame_id || '',
              query: d.query || '',
              requestType: d.request_type || 'sql',
              tablesOrEndpoints: d.tables_or_endpoints || [],
            })),
          }
        : undefined,
      staging: staging
        ? {
            summary: (staging.summary as string) || '',
            stagedRecords: (staging.staged_records as number) || 0,
            frameCount: (staging.frame_count as number) || 0,
          }
        : undefined,
      calculation: calc
        ? {
            summary: (calc.summary as string) || '',
            sqlStepCount: (calc.sql_step_count as number) || 0,
            finalSql: (calc.final_sql as string) || '',
            steps: ((calc.steps as unknown[]) || []).map((step: any) => ({
              tool: step.tool || '',
              sql: step.sql || '',
            })),
          }
        : undefined,
      outputs: outputs
        ? {
            summary: (outputs.summary as string) || '',
            resultTypes: (outputs.result_types as string[]) || [],
            chartCount: (outputs.chart_count as number) || 0,
            chartTitles: (outputs.chart_titles as string[]) || [],
            chartDetails: ((outputs.chart_details as unknown[]) || []).map((c: any) => ({
              title: c.title || '',
              type: c.type || '',
              records: c.records || 0,
              columns: c.columns || [],
              xFields: c.x_fields || [],
              yFields: c.y_fields || [],
              timeUnit: c.time_unit || '',
            })),
            forecastCount: (outputs.forecast_count as number) ?? 0,
            forecastDetails: ((outputs.forecast_details as unknown[]) || []).map((f: any) => ({
              title: f.title || '',
              frameId: f.frame_id || '',
              selectedModel: f.selected_model || '',
              horizon: f.horizon ?? 0,
              timeUnit: f.time_unit || '',
              confidenceLevel: f.confidence_level,
              confidenceLevelPct: f.confidence_level_pct,
              reliability: f.reliability || '',
              historyPoints: f.history_points ?? 0,
              targetColumn: f.target_column || '',
              timeColumn: f.time_column || '',
              periodsCount: f.periods_count ?? 0,
              firstPeriod: f.first_period
                ? {
                    period: f.first_period.period,
                    prediction: f.first_period.prediction,
                    lower: f.first_period.lower,
                    upper: f.first_period.upper,
                  }
                : undefined,
              candidateModels: (f.candidate_models || []).map((m: any) => ({
                model: m.model || '',
                mae: m.mae ?? null,
                rmse: m.rmse ?? null,
                wape: m.wape ?? null,
                p95AbsError: m.p95_abs_error ?? null,
                selectionScore: m.selection_score ?? null,
              })),
              selectedModelMetrics: f.selected_model_metrics
                ? {
                    mae: f.selected_model_metrics.mae ?? null,
                    rmse: f.selected_model_metrics.rmse ?? null,
                    wape: f.selected_model_metrics.wape ?? null,
                    p95AbsError: f.selected_model_metrics.p95_abs_error ?? null,
                    selectionScore: f.selected_model_metrics.selection_score ?? null,
                  }
                : undefined,
              caveats: f.caveats || [],
              dataWindowStart: f.data_window_start || '',
              dataWindowEnd: f.data_window_end || '',
              droppedPoints: f.dropped_points ?? 0,
              rawRows: f.raw_rows ?? 0,
              preparedPoints: f.prepared_points ?? 0,
              seasonLength: f.season_length ?? 0,
            })),
          }
        : undefined,
    };
  };

  return {
    id: String(raw.id || ''),
    threadId: (raw.thread_id as string) || null,
    question: String(raw.question || ''),
    startedAt: String(raw.started_at || ''),
    completedAt: raw.completed_at as string | undefined,
    showAuditLink: typeof raw.show_audit_link === 'boolean' ? raw.show_audit_link : undefined,
    dataSources: ((raw.data_sources as unknown[]) || []).map((s: any) => ({
      frameId: s.frame_id || '',
      title: s.title || 'Query Result',
      connectorId: s.connector_id || '',
      connectorName: s.connector_name || '',
      connectorType: s.connector_type || '',
      rowCount: s.row_count || 0,
      columns: s.columns || [],
      sql: s.sql || '',
      timestamp: s.timestamp || '',
    })),
    steps: ((raw.steps as unknown[]) || []).map((st: any) => ({
      type: st.type || '',
      toolName: st.tool_name || '',
      toolArgs: st.tool_args || {},
      summary: st.summary || '',
      timestamp: st.timestamp || '',
    })),
    charts: ((raw.charts as unknown[]) || []).map((c: any) => ({
      title: c.title || 'Chart',
      chartType: c.chart_type || 'chart',
      rowCount: c.row_count || 0,
      columns: c.columns || [],
      timestamp: c.timestamp || '',
    })),
    story: Object.keys(story).length ? mapStory(story) : undefined,
  };
}

export function stubToWorkspaceArtifact(stub: SlimThreadHistoryResponse['artifacts'][0]): WorkspaceArtifact {
  const base = {
    id: stub.ref,
    eventId: stub.event_id,
    hydration: 'stub' as const,
    createdAt: stub.created_at,
  };

  switch (stub.type) {
    case 'query_result':
      return {
        ...base,
        type: 'query_result',
        title: stub.title,
        frameId: stub.frame_id,
        columns: stub.columns || [],
        rows: [],
        rowCount: stub.row_count || 0,
        elapsedMs: 0,
        sql: '',
        connectorId: '',
        connectorName: stub.connector_name || '',
        connectorType: '',
        connectorCategory: stub.connector_category || '',
        isFileConnector: stub.connector_category === 'file',
        truncated: (stub.row_count || 0) > 0,
      };
    case 'chart':
      return {
        ...base,
        type: 'chart',
        title: stub.title,
        chartType: stub.chart_type || 'bar',
        spec: {},
        rowCount: stub.row_count || 0,
        columns: stub.columns || [],
        chartIntent: {},
      };
    case 'metric_card':
      return {
        ...base,
        type: 'metric_card',
        title: stub.title,
        metricName: stub.metric_name || stub.title,
        metricValue: null,
        columns: [],
        rowCount: 0,
      };
    case 'document':
      return {
        ...base,
        type: 'document',
        title: stub.title,
        playbookId: stub.playbook_id || '',
        runId: stub.run_id || '',
        artifactId: stub.artifact_id,
        format: (stub.format as 'docx' | 'xlsx' | 'pptx' | 'pdf') || 'docx',
      };
    case 'finding':
      return {
        ...base,
        type: 'finding',
        title: stub.title,
        narrative: '',
        dataPoint: {},
        artifactRefs: [],
      };
    default:
      return {
        ...base,
        type: 'query_result',
        title: stub.title,
        columns: [],
        rows: [],
        rowCount: 0,
        elapsedMs: 0,
        sql: '',
        connectorId: '',
        connectorName: '',
        connectorType: '',
        isFileConnector: false,
        truncated: false,
      };
  }
}

export function hydratedPayloadToArtifact(payload: Record<string, unknown>): WorkspaceArtifact {
  const type = String(payload.type || 'query_result');
  const base = {
    id: String(payload.ref || payload.id || ''),
    eventId: String(payload.event_id || ''),
    hydration: 'ready' as const,
    createdAt: String(payload.created_at || new Date().toISOString()),
  };

  if (type === 'chart') {
    return {
      ...base,
      type: 'chart',
      title: String(payload.title || 'Chart'),
      chartType: String(payload.chart_type || 'bar'),
      spec: (payload.spec as Record<string, unknown>) || {},
      rowCount: Number(payload.row_count || 0),
      columns: (payload.columns as string[]) || [],
      chartIntent: (payload.chart_intent as Record<string, unknown>) || {},
    };
  }
  if (type === 'metric_card') {
    return {
      ...base,
      type: 'metric_card',
      title: String(payload.title || 'Metric'),
      metricName: String(payload.metric_name || payload.title || ''),
      metricValue: payload.metric_value,
      columns: (payload.columns as string[]) || [],
      rowCount: Number(payload.row_count || 0),
    };
  }
  if (type === 'document') {
    return {
      ...base,
      type: 'document',
      title: String(payload.title || 'Report'),
      playbookId: String(payload.playbook_id || ''),
      runId: String(payload.run_id || ''),
      artifactId: payload.artifact_id as string | undefined,
      role: payload.role as string | undefined,
      format: (payload.format as 'docx' | 'xlsx' | 'pptx' | 'pdf') || 'docx',
    };
  }
  if (type === 'finding') {
    return {
      ...base,
      type: 'finding',
      title: String(payload.title || 'Finding'),
      narrative: String(payload.narrative || ''),
      dataPoint: (payload.data_point as Record<string, unknown>) || {},
      artifactRefs: (payload.artifact_refs as string[]) || [],
    };
  }

  return {
    ...base,
    type: 'query_result',
    title: String(payload.title || 'Query Result'),
    frameId: payload.frame_id as string | undefined,
    columns: (payload.columns as string[]) || [],
    rows: (payload.rows as unknown[][]) || [],
    rowCount: Number(payload.row_count || 0),
    elapsedMs: Number(payload.elapsed_ms || 0),
    sql: String(payload.sql || ''),
    connectorId: String(payload.connector_id || ''),
    connectorName: String(payload.connector_name || ''),
    connectorType: String(payload.connector_type || ''),
    connectorCategory: String(payload.connector_category || ''),
    isFileConnector: Boolean(payload.is_file_connector),
    truncated: Boolean(payload.truncated),
    columnTypes: payload.column_types as Record<string, string> | undefined,
    columnSemantics: payload.column_semantics as Record<string, string> | undefined,
  };
}

export function mapHistoryMessages(raw: SlimThreadHistoryResponse['messages']): CopilotMessage[] {
  return raw.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content || '',
    timestamp: m.timestamp,
    attachmentIds: m.attachment_ids,
    auditId: m.audit_id,
    statuses: m.statuses || undefined,
    type: m.type as CopilotMessage['type'],
    knowledgeItems: m.knowledge_items?.map((k) => ({
      name: k.name,
      node_type: k.node_type,
      definition: k.definition,
      sql: k.sql,
      node_id: k.node_id,
    })),
  }));
}

export function mapTimeline(
  raw: SlimThreadHistoryResponse['timeline'],
): CopilotTimelineEntry[] {
  return raw.map((entry) => {
    if (entry.type === 'artifact') {
      return { type: 'artifact', artifactId: entry.artifact_ref };
    }
    if (entry.type === 'user' || entry.type === 'assistant') {
      return { type: entry.type, messageId: entry.message_id };
    }
    if (entry.type === 'sql_approval') {
      return { type: 'sql_approval', id: entry.id };
    }
    if (entry.type === 'voice_call_approval') {
      return { type: 'voice_call_approval', id: entry.id };
    }
    if (entry.type === 'voice_call') {
      return { type: 'voice_call', id: entry.id };
    }
    if (entry.type === 'download') {
      return { type: 'download', id: entry.id };
    }
    if (entry.type === 'workbook') {
      return { type: 'workbook', id: entry.id };
    }
    return entry as CopilotTimelineEntry;
  });
}
